import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Users, CalendarDays, Receipt, Briefcase, TrendingUp, Clock,
  AlertCircle, CheckCircle, ArrowRight, Brain, Sparkles,
  Package, Wallet, LogIn, LogOut, IndianRupee, FileText,
  Target, Flame, Award, Eye, EyeOff,
  Quote, Gamepad2, ThumbsUp, PartyPopper, ClipboardList, Trophy,
  Linkedin, Plane, Headphones, Stamp, User
} from 'lucide-react';
import FounderPulseTicker from '../components/FounderPulseTicker';
import IntegrationsHealthWidget from '../components/IntegrationsHealthWidget';
import useSalaryVisibility from '../hooks/useSalaryVisibility';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, icon: Icon, description, trend, color = 'primary', onClick }) => (
  <Card 
    className={`relative overflow-hidden ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} 
    data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
    onClick={onClick}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
          {trend && (
            <div className="flex items-center gap-1 mt-1.5 text-xs text-emerald-600">
              <TrendingUp className="w-3 h-3" />
              <span>{trend}</span>
            </div>
          )}
        </div>
        <div className="p-2.5 rounded-lg bg-muted">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ActivityItem = ({ activity }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'leave': return CalendarDays;
      case 'expense': return Receipt;
      default: return AlertCircle;
    }
  };
  const Icon = getIcon(activity.type);
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="p-2 rounded-md bg-muted">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">{activity.message}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {new Date(activity.created_at).toLocaleDateString()}
        </p>
      </div>
      <Badge variant={activity.status === 'approved' ? 'default' : activity.status === 'rejected' ? 'destructive' : 'secondary'}>
        {activity.status}
      </Badge>
    </div>
  );
};

// ====== EMPLOYEE DASHBOARD (redesigned per approved wireframe) ======
const THOUGHTS = [
  "Great things are built by people who care a little more than others think is wise.",
  "Clarity comes from action, not from thought alone.",
  "The best way to predict the future is to build it.",
  "Small disciplines repeated daily lead to big results over time.",
  "Progress, not perfection — ship, learn, improve.",
  "Your work is a portrait of yourself. Sign it with excellence.",
  "Curiosity is the engine of every breakthrough.",
  "Be the teammate you'd love to work with.",
];

const WidgetTile = ({ title, icon: Icon, value, subtitle, color, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="group text-left rounded-xl border border-border bg-card p-4 transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-primary/40"
  >
    <div className="flex items-center justify-between">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
    <p className="text-sm font-semibold mt-3">{title}</p>
    {value !== undefined && value !== null && <p className="text-xl font-bold mt-0.5">{value}</p>}
    {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
  </button>
);

const EmployeeDashboard = ({ stats, navigate, wordleState, wittiesBalance, user }) => {
  const [thought, setThought] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [linkedinUrl, setLinkedinUrl] = useState('https://www.linkedin.com/company/wisewit/');
  const [attBlocked, setAttBlocked] = useState(false);
  const [attBlockedReason, setAttBlockedReason] = useState('');

  const fetchTodayAttendance = useCallback(async () => {
    if (!user?.id) return;
    try {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
      const res = await axios.get(`${API_URL}/api/attendance?employee_id=${user.id}`, { withCredentials: true });
      const records = Array.isArray(res.data) ? res.data : [];
      setTodayAttendance(records.find(a => a.date === today) || null);
    } catch {
      setTodayAttendance(null);
    }
  }, [user?.id]);

  useEffect(() => {
    axios.get(`${API_URL}/api/thoughts/today`, { withCredentials: true })
      .then(res => setThought(res.data))
      .catch(() => setThought(null));
    axios.get(`${API_URL}/api/company-settings`, { withCredentials: true })
      .then(res => { if (res.data?.linkedin_url) setLinkedinUrl(res.data.linkedin_url); })
      .catch(() => {});
    axios.get(`${API_URL}/api/onboarding/my-status`, { withCredentials: true })
      .then(res => { setAttBlocked(!!res.data?.gated); })
      .catch(() => {});
    fetchTodayAttendance();
  }, [fetchTodayAttendance]);

  const handleCheckIn = async () => {
    setAttLoading(true);
    try {
      await axios.post(`${API_URL}/api/attendance/check-in`, {}, { withCredentials: true });
      toast.success('Checked in successfully!');
      await fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    } finally { setAttLoading(false); }
  };

  const handleCheckOut = async () => {
    setAttLoading(true);
    try {
      await axios.post(`${API_URL}/api/attendance/check-out`, {}, { withCredentials: true });
      toast.success('Checked out successfully!');
      await fetchTodayAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out');
    } finally { setAttLoading(false); }
  };

  const fmtTime = (t) => t ? new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '--:--';

  const thoughtText = thought?.text || THOUGHTS[Math.floor((Date.now() / 86400000)) % THOUGHTS.length];

  const quickActions = [
    { label: 'Apply Leave', icon: CalendarDays, to: '/leaves' },
    { label: 'Regularize Attendance', icon: Clock, to: '/attendance' },
    { label: 'Submit Expense', icon: Receipt, to: '/expenses' },
    { label: 'Request Travel', icon: Plane, to: '/travel' },
    { label: 'Raise HR Request', icon: Headphones, to: '/helpdesk' },
    { label: 'Download Letter', icon: Stamp, to: '/letters' },
    { label: 'Give Recognition', icon: ThumbsUp, to: '/engage' },
    { label: 'Update Profile', icon: User, to: '/profile' },
    { label: 'Download Payslip', icon: Wallet, to: '/my-payslips' },
  ];

  const widgets = [
    { title: 'Wordle', icon: Gamepad2, color: 'bg-emerald-500/15 text-emerald-600', value: wordleState ? `${wordleState.streak || 0} 🔥` : 'Play', subtitle: "Today's puzzle", onClick: () => navigate('/wordle'), testid: 'widget-wordle' },
    { title: 'Praise Wall', icon: ThumbsUp, color: 'bg-pink-500/15 text-pink-600', subtitle: 'Celebrate your peers', onClick: () => navigate('/engage'), testid: 'widget-praise' },
    { title: 'Wishes & Occasions', icon: PartyPopper, color: 'bg-amber-500/15 text-amber-600', subtitle: "Birthdays & festivals", onClick: () => navigate('/wishes'), testid: 'widget-wishes' },
    { title: 'Witties Balance', icon: Award, color: 'bg-violet-500/15 text-violet-600', value: wittiesBalance?.available ?? 0, subtitle: `${wittiesBalance?.total || 0} earned`, onClick: () => navigate('/witties'), testid: 'widget-witties' },
    { title: 'Pulse Surveys', icon: ClipboardList, color: 'bg-blue-500/15 text-blue-600', subtitle: 'Share your feedback', onClick: () => navigate('/engage'), testid: 'widget-pulse' },
    { title: 'Company Achievements', icon: Trophy, color: 'bg-orange-500/15 text-orange-600', subtitle: 'Wisewit milestones', onClick: () => navigate('/programs'), testid: 'widget-achievements' },
  ];

  return (
    <>
      {/* Thought of the Day */}
      <Card className="border-l-4 border-l-primary bg-gradient-to-r from-primary/5 to-transparent" data-testid="thought-of-the-day">
        <CardContent className="p-5 flex items-start gap-4">
          <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
            <Quote className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Thought of the Day</p>
            <p className="text-base font-medium mt-1 leading-relaxed">{thoughtText}</p>
            {thought?.author && <p className="text-xs text-muted-foreground mt-1">— {thought.author}</p>}
          </div>
        </CardContent>
      </Card>

      {/* Check In / Check Out */}
      <Card data-testid="dashboard-attendance-card">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-primary/10 shrink-0">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's Attendance</p>
              <div className="flex items-center gap-4 mt-1">
                <span className="text-sm">
                  <span className="text-muted-foreground">In:</span>{' '}
                  <span className="font-mono font-medium" data-testid="dashboard-check-in-time">{fmtTime(todayAttendance?.check_in)}</span>
                </span>
                <span className="text-sm">
                  <span className="text-muted-foreground">Out:</span>{' '}
                  <span className="font-mono font-medium" data-testid="dashboard-check-out-time">{fmtTime(todayAttendance?.check_out)}</span>
                </span>
              </div>
            </div>
          </div>
          <div>
            {attBlocked ? (
              <div className="text-right" data-testid="dashboard-attendance-blocked">
                <Button disabled variant="outline" className="gap-2 opacity-60 cursor-not-allowed">
                  <LogIn className="w-4 h-4" /> Check In
                </Button>
                <p className="text-[11px] text-amber-600 mt-1 max-w-[220px]">Attendance locked until HR approves your onboarding. <button onClick={() => navigate('/my-onboarding')} className="underline font-medium" data-testid="goto-onboarding-link">Complete it</button></p>
              </div>
            ) : !todayAttendance?.check_in ? (
              <Button onClick={handleCheckIn} disabled={attLoading} className="gap-2" data-testid="dashboard-check-in-btn">
                <LogIn className="w-4 h-4" /> Check In
              </Button>
            ) : !todayAttendance?.check_out ? (
              <Button onClick={handleCheckOut} disabled={attLoading} variant="outline" className="gap-2" data-testid="dashboard-check-out-btn">
                <LogOut className="w-4 h-4" /> Check Out
              </Button>
            ) : (
              <div className="flex items-center gap-2 text-emerald-600" data-testid="dashboard-day-complete">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Day Complete{todayAttendance?.work_mode === 'wfh' ? ' · WFH' : ''}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: engagement widgets + LinkedIn */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Engagement</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="engagement-widgets">
              {widgets.map((w) => <WidgetTile key={w.title} {...w} />)}
            </div>
          </div>

          {/* LinkedIn Posts of Wisewit */}
          <Card data-testid="linkedin-posts-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Linkedin className="w-5 h-5 text-[#0A66C2]" /> LinkedIn Posts of Wisewit
              </CardTitle>
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => window.open(linkedinUrl, '_blank')}>
                Visit <ArrowRight className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                <Linkedin className="w-10 h-10 text-[#0A66C2]/30 mb-2" />
                <p className="text-sm">Latest updates from Wisewit on LinkedIn</p>
                <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => window.open(linkedinUrl, '_blank')}>
                  <Linkedin className="w-3.5 h-3.5" /> Open LinkedIn
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: Quick Actions */}
        <div>
          <Card className="sticky top-4" data-testid="quick-actions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {quickActions.map((a) => (
                <Button
                  key={a.label}
                  variant="outline"
                  className="w-full justify-start gap-2.5 text-sm font-medium"
                  onClick={() => navigate(a.to)}
                  data-testid={`qa-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <a.icon className="w-4 h-4 text-primary" /> {a.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

// ====== ADMIN/HR DASHBOARD ======
const AdminDashboard = ({ stats, activities, techDistribution, navigate, setTechDrillDown, onAttendanceClick, showTile, wordleState }) => (
  <>
    <FounderPulseTicker />
    <IntegrationsHealthWidget />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {showTile('employees') && <StatCard title="Total Employees" value={stats?.total_employees || 0} icon={Users} description="Active employees" />}
      {showTile('team_attendance') && <StatCard title="Today's Attendance" value={`${stats?.attendance_percentage || 0}%`} icon={Clock} description={`${stats?.today_attendance || 0} checked in`} onClick={onAttendanceClick} />}
      {showTile('pending_leaves') && <StatCard title="Pending Leaves" value={stats?.pending_leaves || 0} icon={CalendarDays} description="Awaiting approval" />}
      {showTile('open_requisitions') && <StatCard title="Open Positions" value={stats?.open_positions || 0} icon={Briefcase} description="In hiring pipeline" />}
      {showTile('wordle_streak') && wordleState && (
        <StatCard
          title="Wordle Streak"
          value={`${wordleState.streak || 0} 🔥`}
          icon={Target}
          description={wordleState.status === 'won' ? `Solved in ${wordleState.guesses?.length || 0}` : wordleState.status === 'lost' ? 'Try again tomorrow' : `${wordleState.guesses?.length || 0}/6 attempts today`}
          onClick={() => navigate('/wordle')}
        />
      )}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">Recent Activities</CardTitle>
            <CardDescription>Latest updates from the team</CardDescription>
          </div>
          <Button variant="ghost" size="sm" className="gap-1" onClick={() => navigate('/reports')}>
            View All <ArrowRight className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {activities.length > 0 ? (
            <div>{activities.slice(0, 5).map((activity, index) => <ActivityItem key={index} activity={activity} />)}</div>
          ) : (
            <div className="text-center py-8 text-muted-foreground"><AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No recent activities</p></div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
          <CardDescription>Common tasks at your fingertips</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/attendance')} data-testid="quick-check-in"><Clock className="w-4 h-4" />Check In / Out</Button>
          <Button variant="outline" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/leaves')} data-testid="quick-apply-leave"><CalendarDays className="w-4 h-4" />Apply for Leave</Button>
          <Button variant="outline" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/expenses')} data-testid="quick-submit-expense"><Receipt className="w-4 h-4" />Submit Expense</Button>
          <Button variant="outline" className="w-full justify-start gap-2 text-sm" onClick={() => navigate('/okrs')} data-testid="quick-update-okr"><CheckCircle className="w-4 h-4" />Update OKR</Button>
        </CardContent>
      </Card>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {showTile('tech_stack') && (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Tech Stack Distribution</CardTitle>
          <CardDescription>Technologies used across the team (from Employee profiles)</CardDescription>
        </CardHeader>
        <CardContent>
          {techDistribution?.length > 0 ? (
            <div className="space-y-3">
              {techDistribution.slice(0, 8).map((item, index) => (
                <div key={index} className="space-y-1 cursor-pointer hover:bg-muted/50 rounded p-1 -m-1 transition-colors" onClick={() => setTechDrillDown(item)}>
                  <div className="flex justify-between text-sm"><span className="font-mono text-xs">{item.tech}</span><span className="text-primary font-medium">{item.count} engineers &rarr;</span></div>
                  <Progress value={(item.count / (stats?.total_employees || 1)) * 100} className="h-2" />
                </div>
              ))}
            </div>
          ) : <p className="text-center text-muted-foreground py-4">No tech stacks assigned. Map tech stacks to employees in People &rarr; Employees.</p>}
        </CardContent>
      </Card>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2"><Brain className="w-5 h-5 text-violet-500" />AI Models Expertise</CardTitle>
          <CardDescription>ML/AI models worked on by the team</CardDescription>
        </CardHeader>
        <CardContent>
          {techDistribution?.some(t => ['GPT', 'LLM', 'BERT', 'Transformer', 'Llama', 'Gemini', 'Claude'].some(ai => t.tech?.toLowerCase().includes(ai.toLowerCase()))) ? (
            <div className="flex flex-wrap gap-2">
              {techDistribution.filter(t => ['GPT', 'LLM', 'BERT', 'Transformer', 'Llama', 'Gemini', 'Claude', 'AI', 'ML', 'Deep Learning', 'PyTorch', 'TensorFlow'].some(ai => t.tech?.toLowerCase().includes(ai.toLowerCase()))).map((item, index) => (
                <Badge key={index} variant="outline" className="font-mono text-xs py-1.5 px-3">{item.tech} ({item.count})</Badge>
              ))}
            </div>
          ) : <p className="text-center text-muted-foreground py-4">No AI/ML models data. Add AI-related tech stacks to employees.</p>}
        </CardContent>
      </Card>
    </div>
  </>
);

// ====== MAIN DASHBOARD ======
const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [techDistribution, setTechDistribution] = useState([]);
  const [techDrillDown, setTechDrillDown] = useState(null);
  const [attendanceDetail, setAttendanceDetail] = useState(null);
  const [personaTiles, setPersonaTiles] = useState(null);
  const [wordleState, setWordleState] = useState(null);
  const [wittiesBalance, setWittiesBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';

  // Helper: if persona config not loaded, show all tiles by default; else only show those in the list
  const showTile = (tileKey) => {
    if (!personaTiles) return true;
    return personaTiles.includes(tileKey);
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const promises = [
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/persona/dashboard`, { withCredentials: true }),
        axios.get(`${API_URL}/api/wordle/today`, { withCredentials: true }),
        axios.get(`${API_URL}/api/witties/balance`, { withCredentials: true }),
      ];
      if (isAdmin) {
        promises.push(
          axios.get(`${API_URL}/api/dashboard/recent-activities`, { withCredentials: true }),
          axios.get(`${API_URL}/api/tech-stack/distribution`, { withCredentials: true })
        );
      }
      const results = await Promise.allSettled(promises);
      if (results[0].status === 'fulfilled') setStats(results[0].value.data);
      if (results[1].status === 'fulfilled') setPersonaTiles(results[1].value.data?.tiles || null);
      if (results[2].status === 'fulfilled') setWordleState(results[2].value.data);
      if (results[3].status === 'fulfilled') setWittiesBalance(results[3].value.data);
      if (isAdmin) {
        if (results[4]?.status === 'fulfilled') setActivities(results[4].value.data || []);
        if (results[5]?.status === 'fulfilled') setTechDistribution(results[5].value.data || []);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const fetchAttendanceDetail = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/today-attendance`, { withCredentials: true });
      setAttendanceDetail(res.data);
    } catch (err) {
      toast.error('Failed to fetch attendance details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="welcome-message">Welcome back, {user?.name?.split(' ')[0]}</h1>
          {isAdmin ? (
            <p className="text-muted-foreground text-sm">Here's what's happening at Wisewit today.</p>
          ) : (
            <p className="text-muted-foreground text-sm">
              {stats?.department && stats?.designation
                ? `${stats.designation} · ${stats.department}`
                : "Your personal dashboard"}
            </p>
          )}
        </div>
        <Button onClick={() => navigate('/ai-assistant')} variant="outline" className="gap-2" data-testid="ai-assistant-btn">
          <Brain className="w-4 h-4" />
          Ask AI Assistant
        </Button>
      </div>

      {isAdmin ? (
        <AdminDashboard stats={stats} activities={activities} techDistribution={techDistribution} navigate={navigate} setTechDrillDown={setTechDrillDown} onAttendanceClick={fetchAttendanceDetail} showTile={showTile} wordleState={wordleState} />
      ) : (
        <EmployeeDashboard stats={stats} navigate={navigate} showTile={showTile} wordleState={wordleState} wittiesBalance={wittiesBalance} user={user} />
      )}

      {/* Tech Stack Drill-Down Dialog */}
      <Dialog open={!!techDrillDown} onOpenChange={(open) => !open && setTechDrillDown(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{techDrillDown?.tech} — {techDrillDown?.count} Engineers</DialogTitle></DialogHeader>
          {techDrillDown && (() => {
            const employees = techDrillDown?.employees || [];
            return employees.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {employees.map((emp, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{emp.name?.charAt(0)}</div>
                    <div><p className="text-sm font-medium">{emp.name}</p><p className="text-xs text-muted-foreground">{emp.designation} — {emp.department}</p></div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No detailed employee data available.</p>;
          })()}
        </DialogContent>
      </Dialog>

      {/* Attendance Detail Dialog */}
      <Dialog open={!!attendanceDetail} onOpenChange={(open) => !open && setAttendanceDetail(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Today's Attendance — {attendanceDetail?.checked_in_count}/{attendanceDetail?.total_employees} checked in
            </DialogTitle>
          </DialogHeader>
          {attendanceDetail && (
            <div className="space-y-4">
              {attendanceDetail.checked_in?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-emerald-600 mb-2">Checked In ({attendanceDetail.checked_in.length})</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {attendanceDetail.checked_in.map((emp, i) => (
                      <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/10">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-[10px] font-bold text-emerald-700">{emp.name?.charAt(0)}</div>
                          <div>
                            <p className="text-sm font-medium">{emp.name}</p>
                            <p className="text-[10px] text-muted-foreground">{emp.department}</p>
                          </div>
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">
                          {emp.check_in ? new Date(emp.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                          {emp.check_out && <span className="ml-1 text-amber-600">→ {new Date(emp.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {attendanceDetail.not_checked_in?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-red-500 mb-2">Not Checked In ({attendanceDetail.not_checked_in.length})</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {attendanceDetail.not_checked_in.map((emp, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">{emp.name?.charAt(0)}</div>
                        <div>
                          <p className="text-sm font-medium">{emp.name}</p>
                          <p className="text-[10px] text-muted-foreground">{emp.department}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
